<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { RouterView, useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useInboxStore } from '@/stores/inbox'
import ToastContainer from '@/components/ToastContainer.vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const inboxStore = useInboxStore()

const sidebarOpen = ref(false)
const userMenuOpen = ref(false)
const sidebarCollapsed = ref(localStorage.getItem('sidebarCollapsed') === 'true')

const navigationItems = computed(() => [
  {
    name: 'Screening',
    path: '/screening',
    icon: 'fas fa-filter',
    permission: null
  },
  {
    name: 'Hiring Process',
    path: '/kanban',
    icon: 'fas fa-columns',
    permission: null
  },
  {
    name: 'Interviews',
    path: '/interviews',
    icon: 'fas fa-calendar-check',
    permission: 'view_interviews'
  },
  {
    name: 'Analysis',
    path: '/analyze',
    icon: 'fas fa-file-alt',
    permission: 'create_single_analysis'
  },
  {
    name: 'Positions',
    path: '/positions',
    icon: 'fas fa-briefcase',
    permission: 'view_positions'
  },
  {
    name: 'Candidates',
    path: '/candidates',
    icon: 'fas fa-users',
    permission: 'view_candidates'
  },
  {
    name: 'History',
    path: '/history',
    icon: 'fas fa-history',
    permission: 'view_analyses'
  }
])

const userMenuItems = computed(() => [
  {
    name: 'Settings',
    path: '/settings',
    icon: 'fas fa-cog',
    permission: null
  },
  {
    name: 'Company Admin',
    path: '/admin',
    icon: 'fas fa-users-cog',
    permission: 'manage_organization'
  },
  {
    name: 'Workflows',
    path: '/workflows',
    icon: 'fas fa-project-diagram',
    permission: 'manage_workflows'
  },
  {
    name: 'Master Admin',
    path: '/master-admin',
    icon: 'fas fa-shield-alt',
    requiresMasterAdmin: true
  }
])

const visibleUserMenuItems = computed(() => {
  return userMenuItems.value.filter(item => {
    if (item.requiresMasterAdmin) {
      return authStore.isMasterAdmin
    }
    if (!item.permission) return true
    return authStore.hasPermission(item.permission)
  })
})

const visibleNavItems = computed(() => {
  return navigationItems.value.filter(item => {
    if (!item.permission) return true
    return authStore.hasPermission(item.permission)
  })
})

const isActive = (path: string) => {
  return route.path === path
}

function toggleSidebar() {
  sidebarOpen.value = !sidebarOpen.value
}

function toggleSidebarCollapse() {
  sidebarCollapsed.value = !sidebarCollapsed.value
  localStorage.setItem('sidebarCollapsed', sidebarCollapsed.value.toString())
}

function toggleUserMenu() {
  userMenuOpen.value = !userMenuOpen.value
}

function handleLogout() {
  userMenuOpen.value = false
  authStore.logout()
  router.push('/login')
}

onMounted(() => {
  // Fetch inbox data for badge count
  inboxStore.fetchInbox().catch(error => {
    console.error('Failed to fetch inbox stats:', error)
  })

  // Close sidebar on outside click
  document.addEventListener('click', (e) => {
    const sidebar = document.getElementById('sidebar')
    const toggleBtn = document.getElementById('sidebar-toggle')
    if (
      sidebarOpen.value &&
      sidebar &&
      toggleBtn &&
      !sidebar.contains(e.target as Node) &&
      !toggleBtn.contains(e.target as Node)
    ) {
      sidebarOpen.value = false
    }
  })
})
</script>

<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 text-gray-100">
    <!-- Mobile sidebar backdrop -->
    <div
      v-if="sidebarOpen"
      class="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
      @click="sidebarOpen = false"
    />

    <!-- Sidebar -->
    <aside
      id="sidebar"
      :class="[
        'fixed top-0 left-0 z-50 h-screen bg-gray-900/95 backdrop-blur-lg border-r border-gray-800 transition-all duration-300 ease-in-out shadow-2xl flex flex-col',
        sidebarCollapsed ? 'w-20' : 'w-64',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      ]"
    >
      <!-- Sidebar Header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-800">
        <!-- Expanded: logo image -->
        <img v-if="!sidebarCollapsed" src="/kavana-studio-logo.png" alt="Kavana Studio" class="h-9 w-auto" />
        <!-- Collapsed: icon mark -->
        <div v-else class="relative w-10 h-10 rounded-xl flex items-center justify-center mx-auto" style="background: linear-gradient(135deg, #1e3a5f, #1e40af);">
          <span class="text-lg font-bold text-white">K</span>
          <span class="absolute top-1 right-1 block rounded-full" style="width:6px;height:6px;background:#2abfbf;"></span>
        </div>
        <button
          v-if="!sidebarCollapsed"
          class="p-2 hover:bg-gray-800 rounded-lg transition-colors hidden lg:block"
          @click="toggleSidebarCollapse"
        >
          <i class="fas fa-angles-left text-gray-400" />
        </button>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-1">
        <router-link
          v-for="item in visibleNavItems"
          :key="item.path"
          :to="item.path"
          :class="[
            'flex items-center rounded-lg transition-all duration-200 group relative',
            sidebarCollapsed ? 'justify-center p-3' : 'space-x-3 px-4 py-3',
            isActive(item.path)
              ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/30'
              : 'text-gray-300 hover:bg-gray-800/60 hover:text-white'
          ]"
          :title="sidebarCollapsed ? item.name : ''"
          @click="sidebarOpen = false"
        >
          <i :class="item.icon" class="w-5 text-center" />
          <span v-if="!sidebarCollapsed" class="flex-1">{{ item.name }}</span>
          <span
            v-if="item.badge && !sidebarCollapsed"
            class="bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full"
          >
            {{ item.badge }}
          </span>
          <!-- Collapsed tooltip -->
          <div
            v-if="sidebarCollapsed"
            class="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50"
          >
            {{ item.name }}
          </div>
        </router-link>
      </nav>
      
      <!-- Bottom Section (Inbox tray + User Profile and Expand Button) -->
      <div class="mt-auto">
        <!-- Inbox tray button -->
        <div class="px-4 pb-2">
          <router-link
            to="/inbox"
            :class="[
              'flex items-center rounded-lg transition-all duration-200 group relative',
              sidebarCollapsed ? 'justify-center p-3' : 'space-x-3 px-4 py-3',
              isActive('/inbox')
                ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/30'
                : 'text-gray-300 hover:bg-gray-800/60 hover:text-white'
            ]"
            title="Inbox"
            @click="sidebarOpen = false"
          >
            <i class="fas fa-inbox w-5 text-center" />
            <span v-if="!sidebarCollapsed" class="flex-1">Inbox</span>
            <span
              v-if="inboxStore.stats.pending && !sidebarCollapsed"
              class="bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full"
            >
              {{ inboxStore.stats.pending }}
            </span>
            <span
              v-if="inboxStore.stats.pending && sidebarCollapsed"
              class="absolute top-1 right-1 w-4 h-4 bg-blue-500 text-white text-xs font-bold rounded-full flex items-center justify-center leading-none"
            >
              {{ inboxStore.stats.pending }}
            </span>
            <!-- Collapsed tooltip -->
            <div
              v-if="sidebarCollapsed"
              class="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50"
            >
              Inbox
            </div>
          </router-link>
        </div>
        <!-- User Profile Section -->
        <div class="p-4 border-t border-gray-800">
          <button
            :class="[
              'w-full flex items-center rounded-lg hover:bg-gray-800/60 transition-colors',
              sidebarCollapsed ? 'justify-center p-2' : 'space-x-3 p-3'
            ]"
            @click="toggleUserMenu"
          >
            <div class="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center text-white font-semibold shadow-lg flex-shrink-0">
              {{ authStore.user?.name?.charAt(0) || 'U' }}
            </div>
            <div v-if="!sidebarCollapsed" class="flex-1 text-left min-w-0">
              <div class="text-sm font-medium text-white truncate">
                {{ authStore.user?.name || 'User' }}
              </div>
              <div class="text-xs text-gray-400 truncate">
                {{ authStore.user?.email || '' }}
              </div>
            </div>
            <i v-if="!sidebarCollapsed" :class="['fas fa-chevron-up text-gray-400 text-xs transition-transform', userMenuOpen ? 'rotate-180' : '']" />
          </button>

          <!-- User Menu Dropdown -->
          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <div v-if="userMenuOpen && !sidebarCollapsed" class="absolute bottom-20 left-4 right-4 bg-gray-800/90 backdrop-blur-sm rounded-lg overflow-hidden border border-gray-700 shadow-xl">
              <div class="p-3 border-b border-gray-700">
                <div class="text-xs text-gray-400">{{ authStore.user?.role || 'User' }}</div>
                <div class="text-xs text-gray-400">
                  {{ authStore.user?.organization_name || 'Organization' }}
                </div>
              </div>
              <router-link
                v-for="item in visibleUserMenuItems"
                :key="item.path"
                :to="item.path"
                class="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-700/70 transition-colors flex items-center space-x-2"
                @click="userMenuOpen = false"
              >
                <i :class="item.icon" />
                <span>{{ item.name }}</span>
              </router-link>
              <button
                class="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-700/70 transition-colors flex items-center space-x-2 border-t border-gray-700"
                @click="handleLogout"
              >
                <i class="fas fa-sign-out-alt" />
                <span>Sign Out</span>
              </button>
            </div>
          </transition>
        </div>
        
        <!-- Expand button when collapsed -->
        <div v-if="sidebarCollapsed" class="p-4 border-t border-gray-800 hidden lg:block">
          <button
            class="w-full p-3 hover:bg-gray-800 rounded-lg transition-colors flex items-center justify-center"
            @click="toggleSidebarCollapse"
            title="Expand sidebar"
          >
            <i class="fas fa-angles-right text-gray-400" />
          </button>
        </div>
      </div>
    </aside>

    <!-- Main Content -->
    <div :class="['transition-all duration-300', sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64']">
      <!-- Top Header (Mobile) -->
      <header class="sticky top-0 z-30 bg-gray-900/95 backdrop-blur-lg border-b border-gray-800 lg:hidden shadow-lg">
        <div class="flex items-center justify-between p-4">
          <button
            id="sidebar-toggle"
            class="p-2 rounded-lg hover:bg-gray-800/60 transition-colors"
            @click="toggleSidebar"
          >
            <i class="fas fa-bars text-xl text-gray-300" />
          </button>
          <div class="flex items-center space-x-2">
            <i class="fas fa-cube text-xl text-blue-500" />
            <span class="text-lg font-semibold">Kavana Studio</span>
          </div>
          <div class="w-10" />
        </div>
      </header>

      <!-- Trial Expired Banner -->
      <div
        v-if="authStore.isTrialExpired"
        class="sticky top-0 z-20 flex items-center justify-between gap-3 bg-amber-600/95 backdrop-blur px-4 py-2.5 text-white text-sm shadow-lg"
      >
        <div class="flex items-center gap-2 min-w-0">
          <i class="fas fa-triangle-exclamation flex-shrink-0" />
          <span class="font-medium">Your free trial has ended.</span>
          <span class="hidden sm:inline text-amber-100">Your account is now read-only — data is preserved for 30 days. Contact us to upgrade and restore full access.</span>
        </div>
        <a
          href="mailto:sales@kavanastudio.net?subject=Upgrade%20from%20Trial"
          class="flex-shrink-0 bg-white text-amber-700 font-semibold text-xs px-3 py-1.5 rounded-lg hover:bg-amber-50 transition-colors whitespace-nowrap"
        >
          Upgrade now
        </a>
      </div>

      <!-- Page Content -->
      <main class="min-h-screen">
        <RouterView />
      </main>
    </div>

    <!-- Toast Notifications -->
    <ToastContainer />
  </div>
</template>

<style scoped>
.rotate-180 {
  transform: rotate(180deg);
}
</style>
